import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  addDoc,
  collection,
  getDocs,
  serverTimestamp,
  updateDoc,
  doc,
  writeBatch,
} from "firebase/firestore";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import RelatorioQualificacaoFornecedoresPrint from "./RelatorioQualificacaoFornecedoresPrint";
import { auth, db } from "../Config/firebase";
import { aplicarTenantAtual, consultaTenantAtual } from "../Utils/tenant";
import { AMBIENTE_PRODUCAO, NOME_PLATAFORMA } from "../Config/branding";

const COLLECTION = "tblQualificacaoFornecedores";
const NOTA_MINIMA_QUALIFICADO = 117;
const NOTA_MINIMA_ACOMPANHADO = 91;
const VERSAO = "01";
const APROVADO_EM = "01/09/2025";
const OBJETIVO = "Avaliar e classificar fornecedores de materiais, equipamentos, utensílios e serviços com base em critérios técnicos, comerciais e de atendimento.";
const CRITERIOS_RESULTADO = "0 a 90 pontos (0% a 69%): REJEITADO | 91 a 116 pontos (70% a 89%): ACOMPANHADO | 117 a 130 pontos (90% a 100%): QUALIFICADO";

const CRITERIOS = [
  { campo: "atendimentoTecnico", titulo: "Atendimento técnico e agilidade", peso: 2, limite: 20 },
  { campo: "qualidadeProdutosServicos", titulo: "Qualidade dos produtos/serviços fornecidos", peso: 3, limite: 30 },
  { campo: "conformidadeDocumental", titulo: "Conformidade documental", peso: 2, limite: 20 },
  { campo: "precoCondicoesComerciais", titulo: "Preço e condições comerciais", peso: 1, limite: 10 },
  { campo: "cumprimentoPrazos", titulo: "Cumprimento de prazos", peso: 2, limite: 20 },
  { campo: "garantiasAssistenciaTecnica", titulo: "Garantias ou assistência técnica", peso: 1, limite: 10 },
  { campo: "experienciaRecomendacoes", titulo: "Experiência anterior e recomendações", peso: 1, limite: 10 },
  { campo: "inovacaoSustentabilidadeDigitalizacao", titulo: "Inovações, sustentabilidade, digitalização", peso: 1, limite: 10 },
];

const FORNECEDORES_PADRAO = [
  ["Contenco indústria e comércio LTDA", 20, 30, 20, 10, 20, 10, 10, 10],
  ["Metrotokyo instrumentos de precisão LTDA", 16, 30, 20, 9.5, 18, 9.5, 10, 10],
  ["ATCP do Brasil", 20, 30, 20, 10, 20, 10, 10, 10],
  ...(AMBIENTE_PRODUCAO ? [[NOME_PLATAFORMA, 20, 30, 20, 10, 20, 10, 10, 10]] : []),
  ["Solotest", 20, 30, 20, 10, 20, 10, 10, 10],
  ["Técnica paraiso", 20, 30, 20, 10, 20, 10, 10, 10],
  ["AMC Equipamentos", 20, 30, 20, 10, 20, 10, 10, 10],
  ["Projetest", 16, 25, 18, 10, 20, 10, 10, 10],
].map(([fornecedor, ...notas]) => ({
  fornecedor,
  ...Object.fromEntries(CRITERIOS.map(({ campo }, indice) => [campo, notas[indice]])),
}));

const FORMULARIO_INICIAL = {
  fornecedorId: "",
  fornecedor: "",
  ...Object.fromEntries(CRITERIOS.map(({ campo }) => [campo, ""])),
};

const CADASTRO_FORNECEDOR_INICIAL = {
  fornecedor: "",
  documento: "",
  contato: "",
  telefone: "",
  email: "",
  tipoFornecimento: "",
  observacoes: "",
  situacaoCadastro: "ATIVO",
};

function numeroValido(valor, limite) {
  const texto = String(valor ?? "").trim();
  if (texto === "") return false;

  const numero = Number(texto.replace(",", "."));
  return Number.isFinite(numero) && numero >= 0 && numero <= limite;
}

function calcularNota(dados) {
  return CRITERIOS.reduce((total, { campo, limite }) => {
    const valor = Number(String(dados[campo] || 0).replace(",", "."));
    return total + (Number.isFinite(valor) ? Math.min(Math.max(valor, 0), limite) : 0);
  }, 0);
}

function formatarNota(valor) {
  return Number.isInteger(valor) ? String(valor) : valor.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
}

function situacaoDaNota(nota) {
  if (nota >= NOTA_MINIMA_QUALIFICADO) return "QUALIFICADO";
  if (nota >= NOTA_MINIMA_ACOMPANHADO) return "ACOMPANHADO";
  return "REJEITADO";
}

function corDaSituacao(situacao) {
  if (situacao === "QUALIFICADO") return "success.main";
  if (situacao === "ACOMPANHADO") return "warning.main";
  return "error.main";
}

function severidadeDaNota(nota) {
  if (nota >= NOTA_MINIMA_QUALIFICADO) return "success";
  if (nota >= NOTA_MINIMA_ACOMPANHADO) return "warning";
  return "error";
}

function temQualificacao(registro) {
  return CRITERIOS.every(({ campo }) =>
    registro[campo] !== undefined && registro[campo] !== null && registro[campo] !== ""
  );
}

export default function CadastroQualificacaoFornecedores() {
  const [abaAtual, setAbaAtual] = useState(0);
  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [dialogAberto, setDialogAberto] = useState(false);
  const [registroEditando, setRegistroEditando] = useState(null);
  const [formulario, setFormulario] = useState(FORMULARIO_INICIAL);
  const [dialogFornecedorAberto, setDialogFornecedorAberto] = useState(false);
  const [fornecedorEditando, setFornecedorEditando] = useState(null);
  const [cadastroFornecedor, setCadastroFornecedor] = useState(CADASTRO_FORNECEDOR_INICIAL);
  const [notificacao, setNotificacao] = useState({ aberta: false, mensagem: "", tipo: "success" });
  const [modoRelatorio, setModoRelatorio] = useState(false);
  const cadastrandoPadraoRef = useRef(false);

  async function carregarRegistros() {
    setCarregando(true);
    try {
      const snapshot = await getDocs(consultaTenantAtual(collection(db, COLLECTION)));
      setRegistros(
        snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => {
            const ordemA = Number(a.ordemPadrao || 999);
            const ordemB = Number(b.ordemPadrao || 999);
            return ordemA !== ordemB
              ? ordemA - ordemB
              : String(a.fornecedor || "").localeCompare(String(b.fornecedor || ""), "pt-BR");
          })
      );
    } catch (error) {
      console.error("Erro ao carregar qualificações de fornecedores:", error);
      setNotificacao({ aberta: true, mensagem: "Não foi possível carregar as qualificações.", tipo: "error" });
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregarRegistros();
  }, []);

  const notaAtual = useMemo(() => calcularNota(formulario), [formulario]);
  const qualificacoes = useMemo(() => registros.filter(temQualificacao), [registros]);
  const fornecedoresDisponiveisQualificacao = useMemo(
    () => registros.filter((registro) => !temQualificacao(registro) && registro.situacaoCadastro !== "INATIVO"),
    [registros]
  );
  const registrosFiltrados = useMemo(() => {
    const termo = filtro.trim().toLocaleLowerCase("pt-BR");
    return qualificacoes.filter((registro) =>
      [registro.fornecedor, situacaoDaNota(Number(registro.nota || 0)), registro.nota]
        .some((valor) => contemTextoPesquisa(String(valor || "").toLocaleLowerCase("pt-BR"), termo))
    );
  }, [filtro, qualificacoes]);

  const fornecedoresFiltrados = useMemo(() => {
    const termo = filtro.trim().toLocaleLowerCase("pt-BR");
    return registros.filter((registro) =>
      [registro.fornecedor, registro.documento, registro.contato, registro.tipoFornecimento, registro.situacaoCadastro]
        .some((valor) => contemTextoPesquisa(String(valor || "").toLocaleLowerCase("pt-BR"), termo))
    );
  }, [filtro, registros]);

  function abrirNovo() {
    setRegistroEditando(null);
    setFormulario(FORMULARIO_INICIAL);
    setDialogAberto(true);
  }

  function abrirEdicao(registro) {
    setRegistroEditando(registro);
    setFormulario({
      fornecedorId: registro.id,
      fornecedor: registro.fornecedor || "",
      ...Object.fromEntries(CRITERIOS.map(({ campo }) => [campo, registro[campo] ?? ""])),
    });
    setDialogAberto(true);
  }

  function atualizarCampo(campo, valor) {
    setFormulario((atual) => ({ ...atual, [campo]: valor }));
  }

  function abrirNovoFornecedor() {
    setFornecedorEditando(null);
    setCadastroFornecedor(CADASTRO_FORNECEDOR_INICIAL);
    setDialogFornecedorAberto(true);
  }

  function abrirEdicaoFornecedor(registro) {
    setFornecedorEditando(registro);
    setCadastroFornecedor({
      fornecedor: registro.fornecedor || "",
      documento: registro.documento || "",
      contato: registro.contato || "",
      telefone: registro.telefone || "",
      email: registro.email || "",
      tipoFornecimento: registro.tipoFornecimento || "",
      observacoes: registro.observacoes || "",
      situacaoCadastro: registro.situacaoCadastro || "ATIVO",
    });
    setDialogFornecedorAberto(true);
  }

  function atualizarCadastroFornecedor(campo, valor) {
    setCadastroFornecedor((atual) => ({ ...atual, [campo]: valor }));
  }

  async function salvarFornecedor() {
    const fornecedor = cadastroFornecedor.fornecedor.trim();
    if (!fornecedor) {
      setNotificacao({ aberta: true, mensagem: "Informe o nome ou a razão social do fornecedor.", tipo: "warning" });
      return;
    }

    const duplicado = registros.some((registro) =>
      registro.id !== fornecedorEditando?.id &&
      String(registro.fornecedor || "").trim().toLocaleLowerCase("pt-BR") === fornecedor.toLocaleLowerCase("pt-BR")
    );
    if (duplicado) {
      setNotificacao({ aberta: true, mensagem: "Este fornecedor já está cadastrado.", tipo: "warning" });
      return;
    }

    const dados = {
      ...cadastroFornecedor,
      fornecedor,
      atualizadoEm: serverTimestamp(),
      atualizadoPor: auth.currentUser?.uid || null,
    };

    setSalvando(true);
    try {
      if (fornecedorEditando) {
        await updateDoc(doc(db, COLLECTION, fornecedorEditando.id), dados);
      } else {
        await addDoc(collection(db, COLLECTION), aplicarTenantAtual({
          ...dados,
          criadoEm: serverTimestamp(),
          criadoPor: auth.currentUser?.uid || null,
        }));
      }
      setDialogFornecedorAberto(false);
      setNotificacao({
        aberta: true,
        mensagem: fornecedorEditando ? "Fornecedor atualizado com sucesso." : "Fornecedor cadastrado com sucesso.",
        tipo: "success",
      });
      await carregarRegistros();
    } catch (error) {
      console.error("Erro ao salvar fornecedor:", error);
      setNotificacao({ aberta: true, mensagem: "Não foi possível salvar o fornecedor.", tipo: "error" });
    } finally {
      setSalvando(false);
    }
  }

  async function cadastrarFornecedoresPadrao() {
    if (registros.length > 0 || cadastrandoPadraoRef.current) return;

    cadastrandoPadraoRef.current = true;
    setSalvando(true);
    try {
      const lote = writeBatch(db);
      const registrosCriados = FORNECEDORES_PADRAO.map((fornecedor, indice) => {
        const nota = calcularNota(fornecedor);
        const referencia = doc(collection(db, COLLECTION));
        const dados = aplicarTenantAtual({
          ...fornecedor,
          ordemPadrao: indice + 1,
          nota,
          situacao: situacaoDaNota(nota),
          versao: VERSAO,
          aprovadoEm: "2025-09-01",
          criadoEm: serverTimestamp(),
          criadoPor: auth.currentUser?.uid || null,
          atualizadoEm: serverTimestamp(),
          atualizadoPor: auth.currentUser?.uid || null,
        });
        lote.set(referencia, dados);
        return { id: referencia.id, ...fornecedor, ordemPadrao: indice + 1, nota, situacao: situacaoDaNota(nota) };
      });

      await lote.commit();
      setRegistros(registrosCriados);
      setNotificacao({ aberta: true, mensagem: "Fornecedores padrão cadastrados com sucesso.", tipo: "success" });
    } catch (error) {
      console.error("Erro ao cadastrar fornecedores padrão:", error);
      setNotificacao({ aberta: true, mensagem: "Não foi possível cadastrar os fornecedores padrão.", tipo: "error" });
    } finally {
      cadastrandoPadraoRef.current = false;
      setSalvando(false);
    }
  }

  async function salvar() {
    const fornecedorSelecionado = registros.find((registro) => registro.id === formulario.fornecedorId);
    if (!fornecedorSelecionado) {
      setNotificacao({ aberta: true, mensagem: "Selecione um fornecedor cadastrado.", tipo: "warning" });
      return;
    }

    const fornecedor = String(fornecedorSelecionado.fornecedor || "").trim();

    const criterioInvalido = CRITERIOS.find(({ campo, limite }) => !numeroValido(formulario[campo], limite));
    if (criterioInvalido) {
      setNotificacao({ aberta: true, mensagem: `Informe uma nota entre 0 e ${criterioInvalido.limite} para “${criterioInvalido.titulo}”.`, tipo: "warning" });
      return;
    }

    const nota = calcularNota(formulario);
    const dados = {
      fornecedor,
      ...Object.fromEntries(CRITERIOS.map(({ campo }) => [campo, Number(String(formulario[campo]).replace(",", "."))])),
      nota,
      situacao: situacaoDaNota(nota),
      atualizadoEm: serverTimestamp(),
      atualizadoPor: auth.currentUser?.uid || null,
    };

    setSalvando(true);
    try {
      await updateDoc(doc(db, COLLECTION, fornecedorSelecionado.id), dados);
      setDialogAberto(false);
      setNotificacao({ aberta: true, mensagem: registroEditando ? "Qualificação atualizada com sucesso." : "Fornecedor qualificado com sucesso.", tipo: "success" });
      await carregarRegistros();
    } catch (error) {
      console.error("Erro ao salvar qualificação do fornecedor:", error);
      setNotificacao({ aberta: true, mensagem: "Não foi possível salvar a qualificação.", tipo: "error" });
    } finally {
      setSalvando(false);
    }
  }

  if (modoRelatorio) {
    return (
      <RelatorioQualificacaoFornecedoresPrint
        registros={qualificacoes}
        criterios={CRITERIOS}
        versao={VERSAO}
        aprovadoEm={APROVADO_EM}
        objetivo={OBJETIVO}
        criteriosResultado={CRITERIOS_RESULTADO}
        onVoltar={() => setModoRelatorio(false)}
      />
    );
  }

  return (
    <>
      <Navbar />
      <Box sx={{ p: { xs: 2, md: 4 }, mt: 6, ...(abaAtual === 1 && { height: "calc(100dvh - 48px)", boxSizing: "border-box" }) }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0} sx={{ mb: 2, ...(abaAtual === 1 && { mb: 0, height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", "& > :not(:last-child)": { flexShrink: 0 } }) }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>RQ 7.1 — Cadastro e Qualificação de Fornecedores</Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">Versão {VERSAO} — Aprovado em {APROVADO_EM}</Typography>
              {registros.length === 0 && !carregando && <Button variant="outlined" onClick={cadastrarFornecedoresPadrao} disabled={salvando}>Cadastrar fornecedores padrão</Button>}
              <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => setModoRelatorio(true)} disabled={qualificacoes.length === 0}>Relatório</Button>
              {abaAtual === 0
                ? <Button variant="contained" onClick={abrirNovoFornecedor}>+ Novo fornecedor</Button>
                : <Button variant="contained" onClick={abrirNovo} disabled={fornecedoresDisponiveisQualificacao.length === 0}>+ Nova qualificação</Button>}
            </Box>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 0.9fr) minmax(0, 1.1fr)" }, gap: 1, mt: 1, alignItems: "start" }}>
            <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
              <strong>Objetivo:</strong><br />{OBJETIVO}
            </Typography>
            <Alert severity="info" sx={{ py: 0.5, "& .MuiAlert-message": { py: 0, lineHeight: 1.4 }, "& .MuiAlert-icon": { py: 0 } }}>
              <strong>Critérios do resultado:</strong><br />{CRITERIOS_RESULTADO}
            </Alert>
          </Box>
          <Tabs value={abaAtual} onChange={(_, novaAba) => { setAbaAtual(novaAba); setFiltro(""); }} sx={{ mt: 1, borderBottom: 1, borderColor: "divider" }}>
            <Tab label="Fornecedores" />
            <Tab label="Qualificação" />
          </Tabs>
          <TextField
            label={abaAtual === 0 ? "Pesquisar fornecedor, documento ou contato" : "Pesquisar fornecedor ou situação"}
            value={filtro}
            onChange={(event) => setFiltro(event.target.value)}
            size="small"
            fullWidth
            sx={{ mt: 1 }}
          />
          <Paper variant="outlined" sx={{ mt: 2, overflowX: "auto", ...(abaAtual === 1 && { flex: 1, minHeight: 0, overflowY: "auto" }) }}>
            {abaAtual === 0 ? (
              <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 1100 }}>
                <TableHead><TableRow>
                  <TableCell>Fornecedor</TableCell><TableCell>CNPJ/CPF</TableCell><TableCell>Contato</TableCell>
                  <TableCell>Telefone</TableCell><TableCell>E-mail</TableCell><TableCell>Fornecimento</TableCell>
                  <TableCell align="center">Situação</TableCell><TableCell align="center">Ações</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {carregando && <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={24} /></TableCell></TableRow>}
                  {!carregando && fornecedoresFiltrados.length === 0 && <TableRow><TableCell colSpan={8} align="center">Nenhum fornecedor encontrado.</TableCell></TableRow>}
                  {fornecedoresFiltrados.map((registro) => <TableRow key={registro.id} hover>
                    <TableCell className="lista-dado-principal">{registro.fornecedor}</TableCell>
                    <TableCell>{registro.documento || "—"}</TableCell><TableCell>{registro.contato || "—"}</TableCell>
                    <TableCell>{registro.telefone || "—"}</TableCell><TableCell>{registro.email || "—"}</TableCell>
                    <TableCell>{registro.tipoFornecimento || "—"}</TableCell>
                    <TableCell align="center">{registro.situacaoCadastro || "ATIVO"}</TableCell>
                    <TableCell align="center"><Stack direction="row" justifyContent="center"><Tooltip title="Editar fornecedor"><IconButton size="small" onClick={() => abrirEdicaoFornecedor(registro)}><EditIcon color="primary" /></IconButton></Tooltip><HistoricoCadastro colecao={COLLECTION} documentoId={registro.id} registro={registro} /></Stack></TableCell>
                  </TableRow>)}
                </TableBody>
              </Table>
            ) : (
              <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 1420, tableLayout: "fixed", "& thead th": { fontSize: 12, lineHeight: 1.4, whiteSpace: "normal", overflowWrap: "normal", wordBreak: "normal", hyphens: "none", px: 0.5 }, "& tbody td": { overflowWrap: "anywhere", px: 1 } }}>
                <colgroup>
                  <col style={{ width: 180 }} />
                  {CRITERIOS.map(({ campo }) => <col key={campo} style={{ width: 120 }} />)}
                  <col style={{ width: 60 }} />
                  <col style={{ width: 140 }} />
                  <col style={{ width: 80 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2}>Fornecedor</TableCell>
                    {CRITERIOS.map(({ campo, titulo, limite }) => <TableCell key={campo} align="center" sx={{ "&&": { verticalAlign: "bottom" } }}>{titulo}<br /><Typography component="span" variant="caption">Máx. {limite}</Typography></TableCell>)}
                    <TableCell rowSpan={2} align="center">Nota</TableCell><TableCell rowSpan={2} align="center">Situação</TableCell><TableCell rowSpan={2} align="center">Ações</TableCell>
                  </TableRow>
                  <TableRow>{CRITERIOS.map(({ campo, peso }) => <TableCell key={campo} align="center"><Typography fontSize={12} lineHeight={1.4} fontWeight={800} color="#0d47a1">Peso {peso}</Typography></TableCell>)}</TableRow>
                </TableHead>
                <TableBody>
                  {carregando && <TableRow><TableCell colSpan={12} align="center"><CircularProgress size={24} /></TableCell></TableRow>}
                  {!carregando && registrosFiltrados.length === 0 && <TableRow><TableCell colSpan={12} align="center">Nenhuma qualificação encontrada.</TableCell></TableRow>}
                  {registrosFiltrados.map((registro) => <TableRow key={registro.id} hover>
                    <TableCell className="lista-dado-principal">{registro.fornecedor}</TableCell>
                    {CRITERIOS.map(({ campo }) => <TableCell key={campo} align="center">{formatarNota(Number(registro[campo] || 0))}</TableCell>)}
                    <TableCell align="center" className="lista-dado-destaque">{formatarNota(Number(registro.nota || 0))}</TableCell>
                    <TableCell align="center"><Typography fontSize={12} lineHeight={1.4} fontWeight={700} color={corDaSituacao(situacaoDaNota(Number(registro.nota || 0)))}>{situacaoDaNota(Number(registro.nota || 0))}</Typography></TableCell>
                    <TableCell align="center"><Stack direction="row" justifyContent="center"><Tooltip title="Editar qualificação"><IconButton size="small" onClick={() => abrirEdicao(registro)}><EditIcon color="primary" /></IconButton></Tooltip><HistoricoCadastro colecao={COLLECTION} documentoId={registro.id} registro={registro} /></Stack></TableCell>
                  </TableRow>)}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Paper>
      </Box>

      <Dialog open={dialogAberto} onClose={() => !salvando && setDialogAberto(false)} fullWidth maxWidth="md">
        <DialogTitle>{registroEditando ? "Editar qualificação" : "Qualificar fornecedor"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              label="Fornecedor cadastrado"
              value={formulario.fornecedorId}
              onChange={(event) => {
                const selecionado = registros.find((registro) => registro.id === event.target.value);
                setFormulario((atual) => ({
                  ...atual,
                  fornecedorId: event.target.value,
                  fornecedor: selecionado?.fornecedor || "",
                }));
              }}
              disabled={Boolean(registroEditando)}
              autoFocus
              required
              fullWidth
            >
              {!registroEditando && <option value="">Selecione um fornecedor</option>}
              {(registroEditando ? registros.filter((registro) => registro.id === registroEditando.id) : fornecedoresDisponiveisQualificacao)
                .map((registro) => <option key={registro.id} value={registro.id}>{registro.fornecedor}</option>)}
            </TextField>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              {CRITERIOS.map(({ campo, titulo, limite }) => (
                <TextField
                  key={campo}
                  label={`${titulo} (máx. ${limite})`}
                  value={formulario[campo]}
                  onChange={(event) => {
                    const valor = event.target.value;
                    const numero = Number(valor);
                    if (valor === "" || (Number.isFinite(numero) && numero >= 0 && numero <= limite)) {
                      atualizarCampo(campo, valor);
                    }
                  }}
                  type="number"
                  inputProps={{ min: 0, max: limite, step: 0.1 }}
                  required
                />
              ))}
            </Box>
            <Alert severity={severidadeDaNota(notaAtual)}>
              Nota: <strong>{formatarNota(notaAtual)}</strong> de 130 — {situacaoDaNota(notaAtual)}
              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                {CRITERIOS_RESULTADO}
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setDialogAberto(false)} disabled={salvando}>Cancelar</Button><Button variant="contained" onClick={salvar} disabled={salvando}>{salvando ? "Salvando..." : "Salvar"}</Button></DialogActions>
      </Dialog>

      <Dialog open={dialogFornecedorAberto} onClose={() => !salvando && setDialogFornecedorAberto(false)} fullWidth maxWidth="md">
        <DialogTitle>{fornecedorEditando ? "Editar fornecedor" : "Novo fornecedor"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, pt: 1 }}>
            <TextField label="Nome / Razão social" value={cadastroFornecedor.fornecedor} onChange={(event) => atualizarCadastroFornecedor("fornecedor", event.target.value)} autoFocus required />
            <TextField label="CNPJ/CPF" value={cadastroFornecedor.documento} onChange={(event) => atualizarCadastroFornecedor("documento", event.target.value)} />
            <TextField label="Contato" value={cadastroFornecedor.contato} onChange={(event) => atualizarCadastroFornecedor("contato", event.target.value)} />
            <TextField label="Telefone" value={cadastroFornecedor.telefone} onChange={(event) => atualizarCadastroFornecedor("telefone", event.target.value)} />
            <TextField label="E-mail" type="email" value={cadastroFornecedor.email} onChange={(event) => atualizarCadastroFornecedor("email", event.target.value)} />
            <TextField label="Material ou serviço fornecido" value={cadastroFornecedor.tipoFornecimento} onChange={(event) => atualizarCadastroFornecedor("tipoFornecimento", event.target.value)} />
            <TextField select SelectProps={{ native: true }} label="Situação do cadastro" value={cadastroFornecedor.situacaoCadastro} onChange={(event) => atualizarCadastroFornecedor("situacaoCadastro", event.target.value)}>
              <option value="ATIVO">ATIVO</option><option value="INATIVO">INATIVO</option>
            </TextField>
            <TextField label="Observações" value={cadastroFornecedor.observacoes} onChange={(event) => atualizarCadastroFornecedor("observacoes", event.target.value)} multiline minRows={2} />
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setDialogFornecedorAberto(false)} disabled={salvando}>Cancelar</Button><Button variant="contained" onClick={salvarFornecedor} disabled={salvando}>{salvando ? "Salvando..." : "Salvar"}</Button></DialogActions>
      </Dialog>

      <Snackbar open={notificacao.aberta} autoHideDuration={5000} onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}><Alert severity={notificacao.tipo} onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}>{notificacao.mensagem}</Alert></Snackbar>
    </>
  );
}
